// Pure helpers for the Lead Agent trial-cap banner (RET-1633 / U9). A SIBLING of
// the C8 usage/limit banner — it surfaces a Lead Agent whose FREE-TRIAL token cap
// is near (sighting), reached (capped), re-reached after the one extension
// (extended_capped → CS referral), or ending in the last days (countdown). The
// C8 paid-cohort banner is left untouched; this owns the trial cohort.
//
// Surface + size resolution is REUSED from the pre-acceptance banner so every
// Lead Agent banner stays on the same routes/sizing rules; here we only add the
// thin view resolver over the provider's disjoint role/variant flags.

import {
  resolveBannerSize,
  resolveSurface,
} from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import type {
  tLeadAgentUnlockOffer,
  tLeadAgentUnlockOption,
  tLeadAgentUnlockTierOffer,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';

export { resolveBannerSize, resolveSurface };
export type { tLeadAgentBannerSize } from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';

/** The trial-cap banner variant to render. */
export type tTrialCapBannerVariant = 'sighting' | 'capped' | 'extendedCapped';

/** OWNER = actionable (CTAs); ADMIN = informative (no purchase CTA). */
export type tTrialCapBannerRole = 'owner' | 'admin';

/** The provider's disjoint trial-cap flags (at most one is ever true). */
export type tTrialCapBannerFlags = {
  isTrialSightingForOwner: boolean;
  isTrialSightingForAdmin: boolean;
  isTrialCappedForOwner: boolean;
  isTrialCappedForAdmin: boolean;
  isTrialExtendedCappedForOwner: boolean;
  isTrialExtendedCappedForAdmin: boolean;
};

export type tTrialCapBannerView = {
  variant: tTrialCapBannerVariant;
  role: tTrialCapBannerRole;
};

/**
 * Resolve which (variant, role) the banner should paint from the provider's
 * flags, or null when none is set (Seller/viewer, or no trial-cap phase). The
 * flags are mutually exclusive by construction (the provider derives them from a
 * single `trialCapVariant`), so the order here is only for determinism — at most
 * one branch can match.
 */
export function resolveTrialCapBannerView(
  flags: tTrialCapBannerFlags,
): tTrialCapBannerView | null {
  if (flags.isTrialCappedForOwner) return { variant: 'capped', role: 'owner' };
  if (flags.isTrialCappedForAdmin) return { variant: 'capped', role: 'admin' };
  if (flags.isTrialSightingForOwner) {
    return { variant: 'sighting', role: 'owner' };
  }
  if (flags.isTrialSightingForAdmin) {
    return { variant: 'sighting', role: 'admin' };
  }
  if (flags.isTrialExtendedCappedForOwner) {
    return { variant: 'extendedCapped', role: 'owner' };
  }
  if (flags.isTrialExtendedCappedForAdmin) {
    return { variant: 'extendedCapped', role: 'admin' };
  }
  return null;
}

/**
 * Prototype CTA order: primary unlock, then the deferred option, then the
 * decline link. The server sends `options` in its own order and R2 can drop
 * `accept_deferred` entirely, so the banner sorts by this rank instead of
 * trusting arrival order — otherwise the visual hierarchy of the prototype
 * (solid → outline → link) would flip whenever the payload changed.
 */
const TRIAL_CAP_OPTION_RANK: Record<tLeadAgentUnlockOption, number> = {
  unlock_now: 0,
  accept_deferred: 1,
  decline: 2,
};

export function sortTrialCapOptions(
  options: readonly tLeadAgentUnlockOption[],
): tLeadAgentUnlockOption[] {
  return [...options].sort(
    (a, b) => TRIAL_CAP_OPTION_RANK[a] - TRIAL_CAP_OPTION_RANK[b],
  );
}

/**
 * The tier offer the recommendation card paints, or null when the server sent
 * no suggested tier or no matching per-tier price. Null hides the whole card —
 * a half-rendered price on a paywall is worse than no card at all.
 */
export function resolveRecommendedTierOffer(
  offer: tLeadAgentUnlockOffer | null,
): tLeadAgentUnlockTierOffer | null {
  if (!offer?.suggestedTier) return null;
  return (
    offer.tiers.find((tierOffer) => tierOffer.tier === offer.suggestedTier) ??
    null
  );
}

/**
 * The capacity meter renders only with a usable percentage. `null` (not fetched
 * / errored / no cap) hides the meter and leaves the rest of the banner intact,
 * rather than painting a phantom bar at 0%.
 */
export function resolveMeterPct(capacityPct: number | null): number | null {
  if (capacityPct === null || Number.isNaN(capacityPct)) return null;
  return Math.max(0, Math.min(100, Math.round(capacityPct)));
}
